import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const useFilters = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Default filter and sort values
  const defaultFilters = {
    dateRange: { start: '', end: '' },
    incidentDateRange: { start: '', end: '' },
    searchText: '',
    priority: 'all',
    specificTasks: [],
    status: 'all',
    refFilter: { key: '', value: '' }
  };
  
  const defaultSortConfig = { 
    field: 'created_at', 
    direction: 'desc' 
  };

  // Parse query parameters from URL
  const parseQueryParams = () => {
    const params = new URLSearchParams(location.search);
    
    // Extract filter values from URL params
    const filtersFromParams = {
      dateRange: { 
        start: params.get('startDate') || '', 
        end: params.get('endDate') || '' 
      },
      incidentDateRange: {
        start: params.get('incidentStartDate') || '',
        end: params.get('incidentEndDate') || ''
      },
      searchText: params.get('search') || '',
      priority: params.get('priority') || 'all',
      status: params.get('status') || 'all',
      specificTasks: [],
      refFilter: {
        key: params.get('refKey') || '',
        value: params.get('refValue') || ''
      }
    };
    
    // Log the parsed value immediately
    console.log('Parsed refFilter from URL in useFilters:', filtersFromParams.refFilter);
    
    // Parse specificTasks if present
    if (params.has('tasks')) {
      try {
        // Format is tasks=taskName1:complete,taskName2:incomplete
        const tasksParam = params.get('tasks');
        filtersFromParams.specificTasks = tasksParam.split(',').map(taskStr => {
          const [name, completeStr] = taskStr.split(':');
          return { 
            name, 
            complete: completeStr === 'complete' 
          };
        });
      } catch (e) {
        console.error("Error parsing tasks from URL:", e);
      }
    }
    
    // Extract sort config from URL params
    const sortConfigFromParams = {
      field: params.get('sortField') || defaultSortConfig.field,
      direction: params.get('sortDir') || defaultSortConfig.direction
    };
    
    return { filtersFromParams, sortConfigFromParams };
  };

  // Initialize state with URL params or defaults
  const { filtersFromParams, sortConfigFromParams } = parseQueryParams();
  const [filters, setFilters] = useState(filtersFromParams);
  const [sortConfig, setSortConfig] = useState(sortConfigFromParams);

  // Update URL when filters or sorting change
  useEffect(() => {
    const params = new URLSearchParams();
    
    // Add filter params only if they have values
    if (filters.searchText) params.set('search', filters.searchText);
    if (filters.status !== 'all') params.set('status', filters.status);
    if (filters.priority !== 'all') params.set('priority', filters.priority);
    if (filters.dateRange.start) params.set('startDate', filters.dateRange.start);
    if (filters.dateRange.end) params.set('endDate', filters.dateRange.end);
    if (filters.incidentDateRange.start) params.set('incidentStartDate', filters.incidentDateRange.start);
    if (filters.incidentDateRange.end) params.set('incidentEndDate', filters.incidentDateRange.end);
    if (filters.refFilter.key) params.set('refKey', filters.refFilter.key);
    if (filters.refFilter.value) params.set('refValue', filters.refFilter.value);
    
    // Handle specificTasks
    if (filters.specificTasks.length > 0) {
      const tasksStr = filters.specificTasks
        .map(task => `${task.name}:${task.complete ? 'complete' : 'incomplete'}`)
        .join(',');
      params.set('tasks', tasksStr);
    }
    
    // Add sort params
    params.set('sortField', sortConfig.field);
    params.set('sortDir', sortConfig.direction);
    
    // Update URL without triggering a page reload
    const newSearch = params.toString() ? `?${params.toString()}` : '';
    const newUrl = `${location.pathname}${newSearch}`;
    
    navigate(newUrl, { replace: true });
  }, [filters, sortConfig, location.pathname, navigate]);

  // Clear all filters and update URL
  const clearFilters = () => {
    setFilters(defaultFilters);
  };

  // Handle sorting and update URL
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Filter leads based on current filters
  const filterLeads = (leads) => {
    if (!leads || !Array.isArray(leads)) return [];
    
    return leads.filter(lead => {
      // Search filter
      if (filters.searchText && !matchesSearch(lead, filters.searchText)) {
        return false;
      }
      
      // Status filter
      if (filters.status !== 'all' && lead.status !== filters.status) {
        return false;
      }
      
      // Priority filter
      if (filters.priority !== 'all') {
        const leadPriority = getLeadPriority(lead);
        if (leadPriority !== parseInt(filters.priority)) {
          return false;
        }
      }
      
      // Date range filter (created_at)
      if (filters.dateRange.start && new Date(lead.created_at) < new Date(filters.dateRange.start)) {
        return false;
      }
      
      if (filters.dateRange.end) {
        const endDateObj = new Date(filters.dateRange.end);
        endDateObj.setHours(23, 59, 59, 999);
        if (new Date(lead.created_at) > endDateObj) {
          return false;
        }
      }
      
      // Incident Date range filter (incident_date)
      if (filters.incidentDateRange.start) {
        const leadIncidentDate = lead.incident_date ? new Date(lead.incident_date) : null;
        if (!leadIncidentDate || leadIncidentDate < new Date(filters.incidentDateRange.start)) {
          return false;
        }
      }
      
      if (filters.incidentDateRange.end) {
        const leadIncidentDate = lead.incident_date ? new Date(lead.incident_date) : null;
        const incidentEndDateObj = new Date(filters.incidentDateRange.end);
        incidentEndDateObj.setHours(23, 59, 59, 999); 
        if (!leadIncidentDate || leadIncidentDate > incidentEndDateObj) {
          return false;
        }
      }

      // Ref filter
      if (filters.refFilter.key && lead.ref) {
        let refData;
        try {
          // Try to parse if it's a string
          refData = typeof lead.ref === 'string' ? JSON.parse(lead.ref) : lead.ref;
          
          // Check if the key exists
          if (!refData.hasOwnProperty(filters.refFilter.key)) {
            return false;
          }
          
          // If a value is also specified, check if it matches
          if (filters.refFilter.value && 
              String(refData[filters.refFilter.key]).toLowerCase() !== filters.refFilter.value.toLowerCase()) {
            return false;
          }
        } catch (error) {
          // If parsing fails, exclude this lead
          return false;
        }
      }

      // Specific task status filters
      if (filters.specificTasks && filters.specificTasks.length > 0) {
        for (const taskFilter of filters.specificTasks) {
          const matchingTask = lead.tasks?.find(t => 
            t.name.toLowerCase() === taskFilter.name.toLowerCase()
          );
          
          // If task doesn't exist or completion status doesn't match filter requirement
          if (!matchingTask || matchingTask.complete !== taskFilter.complete) {
            return false;
          }
        }
      }
      
      return true;
    });
  };

  // Sort leads based on current sort config
  const sortLeads = (leads) => {
    if (!leads || !Array.isArray(leads)) return [];
    
    return [...leads].sort((a, b) => {
      let aValue, bValue;
      
      // Handle special cases
      if (sortConfig.field === 'priority') {
        aValue = getLeadPriority(a);
        bValue = getLeadPriority(b);
      } else {
        aValue = a[sortConfig.field];
        bValue = b[sortConfig.field];
      }
      
      // Handle date comparisons
      if (sortConfig.field === 'created_at' || sortConfig.field === 'updated_at' || sortConfig.field === 'incident_date') {
        aValue = new Date(aValue || 0).getTime();
        bValue = new Date(bValue || 0).getTime();
      } else if (sortConfig.field === 'tasks') {
        // Count incomplete tasks for task sorting
        aValue = a.tasks?.filter(t => !t.complete).length || 0;
        bValue = b.tasks?.filter(t => !t.complete).length || 0;
      }
      
      // String comparisons
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      // Null/undefined handling
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      // Actual comparison
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Helper functions
  const matchesSearch = (lead, searchTerm) => {
    if (!searchTerm || !lead) return true;
    searchTerm = searchTerm.toLowerCase();
    
    // List of fields to search in
    const fieldsToSearch = [
      'first_name',
      'last_name',
      'email',
      'phone',
      'condition',
      'details'
    ];
    
    return fieldsToSearch.some(field => {
      const value = lead[field];
      return value && value.toString().toLowerCase().includes(searchTerm);
    });
  };

  const getLeadPriority = (lead) => {
    if (!lead?.tasks || lead.tasks.length === 0) return 0;
    return Math.max(...lead.tasks.map(task => parseInt(task.priority || 0)));
  };

  return {
    filters,
    setFilters,
    clearFilters,
    sortConfig,
    setSortConfig,
    handleSort,
    filterLeads,
    sortLeads
  };
};

export default useFilters; 